import { lazy, Suspense, useEffect, useState } from 'react';
import { useAddCartItem } from '@/features/cart/cartApi';
import { cartErrorKind, cartErrorMessages } from '@/features/cart/cartErrors';
import { CustomerIcon } from '@/features/storefront/CustomerIcon';
import { formatMoney, isAvailable, usableMediaImage, type PublicProduct } from './types';

const ProductDetailModal = lazy(() =>
  import('./ProductDetailModal').then((module) => ({ default: module.ProductDetailModal }))
);

const authoritativeRefreshCodes = new Set([
  'FNB_PUBLIC_PRODUCT_UNAVAILABLE',
  'FNB_PUBLIC_PRICE_CHANGED',
  'FNB_PUBLIC_MODIFIER_INVALID',
  'FNB_PUBLIC_VARIANT_INVALID',
]);

export function ProductCard({
  product,
  storeCode,
  browseStoreCode,
  priority = false,
  editorialBadge,
  allowDirectAdd = true,
  onAuthoritativeChange,
}: {
  product: PublicProduct;
  storeCode?: string;
  /** A browsing preference only. It is intentionally never used for cart authorization. */
  browseStoreCode?: string;
  priority?: boolean;
  editorialBadge?: string;
  allowDirectAdd?: boolean;
  onAuthoritativeChange?: () => void;
}) {
  const [detailOpen, setDetailOpen] = useState(false);
  const available = isAvailable(product.availability);
  const configurable =
    !allowDirectAdd || Boolean(product.variants?.length || product.modifierGroups?.length);
  const add = useAddCartItem(storeCode);
  const image = usableMediaImage(
    product.media?.find((item) => item.isPrimary) ?? product.media?.[0]
  );
  const badge = product.moodName?.trim() || editorialBadge?.trim();

  const addDirectly = () => {
    if (!storeCode || !product.id || configurable || !available) return;
    add.mutate({ productId: product.id, modifierOptionIds: [], quantity: 1 });
  };

  useEffect(() => {
    const code = (add.error as { publicCode?: string } | null)?.publicCode;
    if (add.isError && code && authoritativeRefreshCodes.has(code)) onAuthoritativeChange?.();
  }, [add.error, add.isError, onAuthoritativeChange]);

  return (
    <article
      className={`product-card${available ? '' : ' is-unavailable'}`}
      aria-disabled={!available}
    >
      {available && product.id && (
        <button
          className="product-card-cover-button"
          type="button"
          aria-label={`Xem ${product.name ?? 'món'}, ${formatMoney(product.fromPrice, product.currency)}`}
          onClick={() => setDetailOpen(true)}
        />
      )}
      <div className="product-image-wrap">
        {badge && <span className="product-editorial-badge">{badge}</span>}
        {image ? (
          <img
            src={image}
            width="400"
            height="300"
            loading={priority ? 'eager' : 'lazy'}
            decoding="async"
            sizes="(max-width: 560px) 82vw, (max-width: 900px) 44vw, 280px"
            alt=""
          />
        ) : (
          <span className="product-image-fallback" aria-hidden="true">
            Dream Up
          </span>
        )}
      </div>
      <div className="product-card-body">
        <h3>{product.name ?? 'Món chưa đặt tên'}</h3>
        {product.description && <p>{product.description}</p>}
        <div className="product-card-footer">
          <strong>{formatMoney(product.fromPrice, product.currency)}</strong>
          {available && product.id ? (
            !storeCode || configurable ? (
              <span className="product-card-action" aria-hidden="true">
                <CustomerIcon name="chevron" />
                <span aria-hidden="true">→</span>
              </span>
            ) : (
              <button
                className="product-card-action"
                type="button"
                aria-label={`Thêm ${product.name ?? 'món'} vào giỏ`}
                disabled={add.isPending}
                onClick={addDirectly}
              >
                <CustomerIcon name="plus" />
                <span aria-hidden="true">{add.isPending ? '…' : '+'}</span>
              </button>
            )
          ) : (
            <span className="unavailable-label">Tạm hết</span>
          )}
        </div>
        {add.isError && (
          <p className="product-card-error" role="alert">
            {cartErrorMessages[cartErrorKind(add.error)]}
          </p>
        )}
      </div>

      {detailOpen && product.id && (
        <Suspense fallback={null}>
          <ProductDetailModal
            productId={product.id}
            productName={product.name}
            storeCode={storeCode}
            browseStoreCode={browseStoreCode}
            onClose={() => setDetailOpen(false)}
          />
        </Suspense>
      )}
    </article>
  );
}
